import { addingConditionAutomationFlag } from '../../redux/slices/automation';
import { Close } from '@mui/icons-material';
import { conditionAutomationSchema } from '../../schema/automation_schema';
import CustomDropdown from '../../components/reusable/dropdown';
import CustomInput from '../../components/reusable/input';
import LabelWrapper from '../../components/wrapper/label';
import { updateBuildAutomationThunk } from '../../redux/thunks/automation';
import { addNode, editSelectedNode, settingSelectedEdge, settingSelectedNode } from '../../redux/slices/workflow';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

type TriggerType = 'replied' | 'not_replied' | 'pos_replied' | 'neg_replied' | 'deal';
type DealActions = 'deal_name' | 'company_name' | 'company_phone_number' | 'company_email_address' | 'company_address' | 'company_city' | 'company_website'
    | 'contact_email_address' | 'contact_name' | 'contact_phone_number' | 'custom_field_1' | 'custom_field_2' | 'custom_field_3' | 'custom_field_4' | 'custom_field_5';
type ComparisonType = 'equal' | 'not_equal' | 'contain' | 'not_contain';
interface ConditionObject {
    type: 'phone' | 'condition' | 'email' | 'timer',
    action_name: string;
    trigger: {
        type: TriggerType;
        emailReference?: string;
        dealData?: {
            selectedAction: DealActions;
            comparisons: ComparisonType;
            value: string;
        }
    }
}

const defaultTrigger: {
    type: TriggerType;
    emailReference?: string;
    dealData?: {
        selectedAction: DealActions;
        comparisons: ComparisonType;
        value: string;
    }
} = {
    type: 'replied',
    emailReference: '',
    dealData: {
        selectedAction: 'deal_name',
        comparisons: 'equal',
        value: ''
    }
};

const SetConditionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { nodes, selectedNode } = useSelector((state: RootState) => state.workflow);
    const { loaders } = useSelector((state: RootState) => state.automation);
    const initialValues: ConditionObject = {
        type: 'condition',
        action_name: (selectedNode?.data?.action_name as string) || '',
        trigger: {
            ...defaultTrigger,
            ...(selectedNode?.data?.trigger || defaultTrigger)
        }
    };

    const extractEmails = () => {
        const emails: {
            label: string;
            value: string;
        }[] = [];
        nodes.forEach((node) => {
            if (node.type === 'email' || node.type === 'emailConditionItem') {
                emails.push({ label: node.data.action_name as string, value: node.id });
            }
        });
        return emails;
    };

    const emailOptions = extractEmails();

    const handleBackNavigation = () => {
        dispatch(settingSelectedNode(null));
        dispatch(settingSelectedEdge(null));
        dispatch(addingConditionAutomationFlag(false));
    };

    const handleSubmit = async (value: ConditionObject) => {
        if (selectedNode) {
            dispatch(editSelectedNode(value));
        } else {
            dispatch(addNode(value));
        }
        try {
            await dispatch(updateBuildAutomationThunk()).unwrap();
        } catch (e) {
            // Catch errors
        }
        handleBackNavigation();
    };

    return (<Box width="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box></Box>
            <Typography variant="h3" fontWeight="bold" textAlign="center">{selectedNode ? 'Update' : 'Set'} Condition</Typography>
            <IconButton disabled={loaders.isUpdating}
                onClick={handleBackNavigation}><Close /></IconButton>
        </Box>
        <Box minWidth="600px" maxWidth="600px" mx="auto" my={4}>
            <Formik
                initialValues={initialValues}
                validationSchema={conditionAutomationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {/* Input for stage name */}
                            <CustomInput
                                value={values.action_name}
                                onChange={handleChange('action_name')}
                                onBlur={handleBlur('action_name')}
                                error={touched.action_name && errors.action_name}
                                fullWidth
                                label="Action name:"
                                placeholder="Type here"
                                variant="outlined"
                                disabled={loaders.isUpdating}
                            />
                            {/* Input for days due */}
                            <Box>
                                <LabelWrapper label="Select Trigger" />
                                <CustomDropdown
                                    options={[
                                        { label: 'Email Replied', value: 'replied' },
                                        { label: 'Email Not Replied', value: 'not_replied' },
                                        { label: 'Replied With Positive Sentiment', value: 'pos_replied' },
                                        { label: 'Replied With Negative Sentiment', value: 'neg_replied' },
                                        { label: 'Deal Data', value: 'deal' }
                                    ]}
                                    value={values.trigger.type}
                                    onChange={(e) => {
                                        setFieldValue('trigger.type', e.target.value);
                                    }}
                                    disabled={loaders.isUpdating}
                                    sx={{ marginBottom: '0' }}
                                />
                            </Box>
                            {values.trigger.type !== 'deal' && (
                                <Box>
                                    <LabelWrapper label="Select Email" />
                                    <CustomDropdown
                                        options={emailOptions}
                                        value={values.trigger.emailReference || ''}
                                        disabled={loaders.isUpdating}
                                        onChange={(e) => setFieldValue('trigger.emailReference', e.target.value)}
                                        error={Boolean(touched.trigger?.emailReference && errors.trigger?.emailReference)}
                                    />
                                </Box>
                            )}
                            {values.trigger.type === 'deal' && (
                                <Box display="flex" gap={2}>
                                    <Box width="100%">
                                        <LabelWrapper label="Select Value" />
                                        <CustomDropdown
                                            options={[
                                                { label: 'Deal Name', value: 'deal_name' },
                                                { label: 'Company Name', value: 'company_name' },
                                                { label: 'Company Phone', value: 'company_phone_number' },
                                                { label: 'Company Email', value: 'company_email_address' },
                                                { label: 'Company Address', value: 'company_address' },
                                                { label: 'Company City', value: 'company_city' },
                                                { label: 'Company Website', value: 'company_website' },
                                                { label: 'Contact Email', value: 'contact_email_address' },
                                                { label: 'Contact Name', value: 'contact_name' },
                                                { label: 'Contact Phone', value: 'contact_phone_number' },
                                                { label: 'Custom Field 1', value: 'custom_field_1' },
                                                { label: 'Custom Field 2', value: 'custom_field_2' },
                                                { label: 'Custom Field 3', value: 'custom_field_3' },
                                                { label: 'Custom Field 4', value: 'custom_field_4' },
                                                { label: 'Custom Field 5', value: 'custom_field_5' }
                                            ]}
                                            disabled={loaders.isUpdating}
                                            value={values.trigger.dealData?.selectedAction as string}
                                            onChange={(e) => setFieldValue('trigger.dealData.selectedAction', e.target.value)}
                                        />
                                    </Box>

                                    <Box width="100%">
                                        <LabelWrapper label="Select Comparison" />
                                        <CustomDropdown
                                            options={[
                                                { label: 'Equal', value: 'equal' },
                                                { label: 'Not Equal', value: 'not_equal' },
                                                { label: 'Contains', value: 'contain' },
                                                { label: 'Does Not Contain', value: 'not_contain' }
                                            ]}
                                            disabled={loaders.isUpdating}
                                            value={values.trigger.dealData?.comparisons as string}
                                            onChange={(e) => setFieldValue('trigger.dealData.comparisons', e.target.value)}
                                        />
                                    </Box>

                                    <Box width="100%">
                                        <CustomInput
                                            value={values.trigger.dealData?.value}
                                            onChange={handleChange('trigger.dealData.value')}
                                            placeholder="Enter Value"
                                            label="Enter Value"
                                            fullWidth
                                            disabled={loaders.isUpdating}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {/* Action buttons for cancel and submit */}
                            <Box display="flex" flexDirection="row-reverse" gap={2} mt={2}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loaders.isUpdating}
                                >
                                    {selectedNode && loaders.isUpdating ? 'Updating' : selectedNode ? 'Update' : loaders.isUpdating ? 'Creating' : 'Create'} action
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleBackNavigation}
                                    disabled={loaders.isUpdating}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>
    </Box>);
};

export default SetConditionComponent;